/* eslint-disable react/jsx-props-no-spreading */
import { Filter, Form } from '@/components'
import { clinicalCareReportStatuses, clinicalCareStatus } from '@/constants/clinicalCares'
import useQuery from '@/hooks/useQuery'
import categoriesService from '@/services/api/categories'
import clinicalCares from '@/services/api/clinicalCares'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'

function getClinicId(licensing) {
  return licensing?.licensedId || licensing?.licensed?.id
}

function FilterClinicalCares({ dateRef, lockDateRef }) {
  const query = useQuery()
  const [categories, setCategories] = useState([])
  const [breeds, setBreeds] = useState([])
  const [clinics, setClinics] = useState([])

  const fetchBreeds = useCallback(async (categoryId) => {
    if (!categoryId) {
      setBreeds([])
      return
    }
    const { data } = await categoriesService.breedsByCategoryId(categoryId)
    setBreeds(data)
  }, [])

  useEffect(() => {
    async function fetchOptions() {
      const [{ data: categoriesData }, { data: clinicsData }] = await Promise.all([
        categoriesService.list(),
        clinicalCares.allowedClinics(),
      ])
      setCategories(categoriesData)
      setClinics(clinicsData)
    }
    fetchOptions()
  }, [])

  const initialCategoryId = query?.q?.animalCategoryId
  useEffect(() => {
    fetchBreeds(initialCategoryId)
  }, [fetchBreeds, initialCategoryId])

  return (
    <Filter
      key={`filter-${dateRef}`}
      initialValues={{
        dateRef: dateRef || '',
        datePeriod: [null, null],
        animalCategoryId: '',
        animal: {},
        animalGender: '',
        statusClinicalCare: '',
        clinicId: '',
      }}
      title="Filtrar atendimentos clínicos"
      onClear={() => setBreeds([])}
      renderForm={({ values, setFieldValue }) => (
        <>
          <div className="divider text-center" data-content="Períodos" />
          {!lockDateRef ? (
            <Form.Group>
              <Form.Label className="text-sm" htmlFor="dateRef">
                Selecione o período de referência para o filtro
              </Form.Label>
              <Form.ValidationField name="dateRef">
                {({ field }) => (
                  <Form.Select
                    {...field}
                    id="dateRef"
                    className="select-sm"
                    placeholder="Data de referência"
                  >
                    <option
                      value=""
                      disabled
                      className="text-italic text-gray-500"
                    >
                      Selecione
                    </option>
                    <option value="createdAt">Solicitado em</option>
                    <option value="forwardedAt">Encaminhado em</option>
                    <option value="scheduledTo">Agendado para</option>
                    <option value="hospitalizedAt">Internado em</option>
                    <option value="medicalReleaseAt">Alta médica em</option>
                    <option value="pulloutAt">Retirada em</option>
                    <option value="caughtAt">Saída em</option>
                    <option value="doneAt">Concluído em</option>
                    <option value="canceledAt">Cancelado em</option>
                    <option value="deathAt">Falecido em</option>
                  </Form.Select>
                )}
              </Form.ValidationField>
            </Form.Group>
          ) : null}
          <Form.Group>
            <Form.Label className="text-sm" htmlFor="datePeriod">
              De - Até
            </Form.Label>
            <Form.DatePickerField
              placeholderText="Data início - Data fim"
              dateFormat="dd/MM/yyyy"
              startDate={
                values.datePeriod?.[0] ? new Date(values.datePeriod[0]) : null
              }
              endDate={
                values.datePeriod?.[1] ? new Date(values.datePeriod[1]) : null
              }
              className="form-input input-sm"
              selectsRange
              isClearable
              disabled={!values.dateRef}
              name="datePeriod"
            />
          </Form.Group>
          <div className="divider text-center" data-content="Animal" />
          <Form.Group>
            <Form.Label htmlFor="animalCategoryId">Espécie do pet</Form.Label>
            <Form.ValidationField name="animalCategoryId">
              {({ field }) => (
                <Form.Select
                  {...field}
                  onChange={({ target: { value } }) => {
                    setFieldValue('animalCategoryId', value)
                    setFieldValue('animal', {})
                    fetchBreeds(value)
                  }}
                  id="animalCategoryId"
                  className="select-sm"
                  placeholder="Espécie do pet"
                >
                  <option value="">Todos</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.description}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.ValidationField>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="animalBreedId">Raça do pet</Form.Label>
            <Form.ValidationField name="animal.breedId">
              {({ field }) => (
                <Form.Select
                  {...field}
                  value={field.value || ''}
                  onChange={({ target: { value } }) => {
                    setFieldValue('animal', value ? { breedId: value } : {})
                  }}
                  id="animalBreedId"
                  className="select-sm"
                  disabled={!values.animalCategoryId}
                >
                  <option value="">
                    {values.animalCategoryId ? 'Todas' : 'Selecione a espécie'}
                  </option>
                  {breeds.map((breed) => (
                    <option key={breed.id} value={breed.id}>
                      {breed.description}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.ValidationField>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="animalGender">Gênero do pet</Form.Label>
            <Form.ValidationField name="animalGender">
              {({ field }) => (
                <Form.Select
                  {...field}
                  id="animalGender"
                  className="select-sm"
                  placeholder="Gênero do pet"
                >
                  <option value="">Todos</option>
                  <option value="male">Macho</option>
                  <option value="female">Fêmea</option>
                </Form.Select>
              )}
            </Form.ValidationField>
          </Form.Group>
          <div className="divider text-center" data-content="Atendimento" />
          <Form.Group>
            <Form.Label htmlFor="statusClinicalCare">Status</Form.Label>
            <Form.ValidationField name="statusClinicalCare">
              {({ field }) => (
                <Form.Select
                  {...field}
                  id="statusClinicalCare"
                  className="select-sm"
                  placeholder="Status do atendimento"
                >
                  <option value="">Todos</option>
                  {clinicalCareReportStatuses.map((status) => (
                    <option key={status} value={status}>
                      {clinicalCareStatus[status]}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.ValidationField>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="clinicId">Clínica</Form.Label>
            <Form.ValidationField name="clinicId">
              {({ field }) => (
                <Form.Select
                  {...field}
                  id="clinicId"
                  className="select-sm"
                  placeholder="Clínica"
                >
                  <option value="">Todas</option>
                  {clinics.map((licensing) => (
                    <option key={licensing.id} value={getClinicId(licensing)}>
                      {licensing?.licensed?.fullName}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.ValidationField>
          </Form.Group>
        </>
      )}
    />
  )
}

FilterClinicalCares.propTypes = {
  dateRef: PropTypes.string,
  lockDateRef: PropTypes.bool,
}

FilterClinicalCares.defaultProps = {
  dateRef: '',
  lockDateRef: false,
}

export default FilterClinicalCares
